import {
  FormControlLabel,
  Grid,
  Typography,
  Chip,
  TextField,
  Divider,
  Box,
  makeStyles,
  createStyles,
  useTheme,
} from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { Autocomplete } from "@material-ui/lab";
import Authorities from "../../../../../auth/authorities";
import withAuthority from "../../../../../components/Auth/withAuthority";
import CardCommon from "../../../../../components/card/CardCommon";
import ButtonCommon from "../../../../../components/buttons/ButtonCommon";
import _ from "lodash";
import SwitchCommon from "../../../../../components/switches/SwitchCommon";
import { DeliveryTpe, submitButtonName } from "../../../../../utils/enum";
import AvailabilitySelection from "../../../../../components/time/AvailabilitySelection";
import { CustomTheme } from "../../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autocomplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.background.entity_border}`,
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

// Platform selector object
export const platformSelectorObj: any = {
  "walk in": { id: "walk in", label: "Walk In" },
  phone: { id: "phone", label: "Phone" },
  kiosk: { id: "kiosk", label: "Kiosk" },
  online: { id: "online", label: "Online" },
};

// Channel selector list
export const channelSelectorList = [
  { id: "eatpresto", label: "Eat Presto" },
  { id: "deliverect", label: "Deliverect" },
  { id: "justeat", label: "Just Eat" },
  { id: "deliveroo", label: "Deliveroo" },
  { id: "ubereats", label: "Uber Eats" },
];

interface PlatformOption {
  id: string;
  label: string;
}

interface ChannelOption {
  id: string;
  label: string;
}

export interface AvailabilityModalProps {
  deliveryTypesList: any;
  handleSelectDeliveryTypes: any;
  isDeliveryTypeEmpty: any;
  availabilityList: any;
  handleSubmitAvailability: any;
  isActive: any;
  handleSwitchActivate: any;
  handleDisable: any;
  handleSubmitData: any;
  // New props for platforms and channels
  platformList?: any[];
  setPlatformList?: (value: any[]) => void;
  channelList?: any[];
  setChannelList?: (value: any[]) => void;
  platformListInitial?: any[];
  channelListInitial?: any[];
}

/* This component shows the 'availability' and delivery type of menu information. */
const AvailabilityModal: React.FunctionComponent<AvailabilityModalProps> = ({
  deliveryTypesList,
  handleSelectDeliveryTypes,
  isDeliveryTypeEmpty,
  availabilityList,
  handleSubmitAvailability,
  isActive,
  handleSwitchActivate,
  handleDisable,
  handleSubmitData,
  platformList = [],
  setPlatformList = () => {},
  channelList = [],
  setChannelList = () => {},
  platformListInitial = [],
  channelListInitial = [],
}) => {
  const [availabilityErrorMessage, setAvailabilityErrorMessage] = useState("");
  const classes = useStyles();

  // Get all platform IDs
  const allPlatformIds = Object.values(platformSelectorObj).map(
    (p: any) => p.id,
  );
  const allChannelIds = channelSelectorList.map((c) => c.id);

  // ✅ Function to determine if platforms should show all selected
  const shouldShowAllPlatforms = () => {
    // If platformList is empty OR platformListInitial is empty AND no platform data exists
    if (platformList.length === 0 && platformListInitial.length === 0) {
      return true;
    }
    return false;
  };

  // ✅ Function to determine if channels should show all selected
  const shouldShowAllChannels = () => {
    // If channelList is empty OR channelListInitial is empty AND no channel data exists
    if (channelList.length === 0 && channelListInitial.length === 0) {
      return true;
    }
    return false;
  };

  // Convert platform list to objects with label
  const getPlatformObjects = (ids: string[]): PlatformOption[] => {
    // ✅ If no platforms selected, return all platforms (show all chips)
    if (ids.length === 0 && shouldShowAllPlatforms()) {
      return Object.values(platformSelectorObj);
    }
    return ids.map((id) => ({
      id,
      label: platformSelectorObj[id]?.label || id,
    }));
  };

  // Convert channel list to objects with label
  const getChannelObjects = (ids: string[]): ChannelOption[] => {
    // ✅ If no channels selected, return all channels (show all chips)
    if (ids.length === 0 && shouldShowAllChannels()) {
      return channelSelectorList;
    }
    return ids.map((id) => ({
      id,
      label: channelSelectorList.find((c) => c.id === id)?.label || id,
    }));
  };

  // Get platform objects for Autocomplete
  const platformOptions: PlatformOption[] = Object.values(platformSelectorObj);
  const selectedPlatforms: PlatformOption[] = getPlatformObjects(platformList);

  // Get channel objects for Autocomplete
  const channelOptions: ChannelOption[] = channelSelectorList;
  const selectedChannels: ChannelOption[] = getChannelObjects(channelList);

  // Handle platform change
  const handlePlatformChange = (
    event: React.ChangeEvent<{}>,
    selectedOptions: PlatformOption[],
  ) => {
    const uniqueSelected = Array.from(
      new Map(selectedOptions.map((opt) => [opt.id, opt])).values(),
    );
    const selectedIds = uniqueSelected.map((option) => option.id);
    setPlatformList(selectedIds);
  };

  // Handle channel change
  const handleChannelChange = (
    event: React.ChangeEvent<{}>,
    selectedOptions: ChannelOption[],
  ) => {
    const uniqueSelected = Array.from(
      new Map(selectedOptions.map((opt) => [opt.id, opt])).values(),
    );
    const selectedIds = uniqueSelected.map((option) => option.id);
    setChannelList(selectedIds);
  };

  const theme = useTheme<CustomTheme>();

  return (
    <CardCommon backgroundColor={"entity_background"}>
      <Grid
        container
        style={{
          marginTop: "8px",
          marginLeft: "8px",
        }}
      >
        {/* Availability delivery type selection switches */}
        <Grid item xs={12} sm={8} md={6}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingRight: "28px",
            }}
          >
            <FormControlLabel
              style={{ marginLeft: "0px", marginRight: "0px" }}
              value="dinein"
              control={<SwitchCommon />}
              label={
                <Typography variant="caption" color="textSecondary">
                  Dine In
                </Typography>
              }
              name="dinein"
              checked={
                !_.isEmpty(deliveryTypesList)
                  ? deliveryTypesList.includes(DeliveryTpe.DINEIN)
                  : false
              }
              labelPlacement="bottom"
              onChange={handleSelectDeliveryTypes}
            />
            <FormControlLabel
              value="delivery"
              control={<SwitchCommon />}
              label={
                <Typography variant="caption" color="textSecondary">
                  Delivery
                </Typography>
              }
              name="delivery"
              checked={
                !_.isEmpty(deliveryTypesList)
                  ? deliveryTypesList.includes(DeliveryTpe.DELIVERY)
                  : false
              }
              labelPlacement="bottom"
              onChange={handleSelectDeliveryTypes}
            />
            <FormControlLabel
              value="takeout"
              name="takeout"
              control={<SwitchCommon />}
              label={
                <Typography variant="caption" color="textSecondary">
                  Takeout
                </Typography>
              }
              checked={
                !_.isEmpty(deliveryTypesList)
                  ? deliveryTypesList.includes(DeliveryTpe.TAKEOUT)
                  : false
              }
              labelPlacement="bottom"
              onChange={handleSelectDeliveryTypes}
            />
          </div>
          {_.isEqual(isDeliveryTypeEmpty, true) && (
            <div
              style={{
                display: "flex",
                justifyContent: "start",
                marginBottom: "3px",
              }}
            >
              <Typography
                align="left"
                variant="caption"
                style={{ color: "red" }}
              >
                Please select a least one delivery option.
              </Typography>
            </div>
          )}
        </Grid>

        {/* Availability active button */}
        <Grid
          item
          xs={12}
          sm={4}
          md={6}
          style={{
            display: "flex",
            justifyContent: "end",
            paddingRight: "40px",
          }}
        >
          <FormControlLabel
            style={{ marginLeft: "0px", marginRight: "0px" }}
            control={<SwitchCommon />}
            checked={isActive}
            onChange={handleSwitchActivate}
            label={
              <Typography variant="caption" color="textSecondary">
                Active
              </Typography>
            }
            labelPlacement="bottom"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider
            style={{
              backgroundColor: "rgba(255,255,255,0.08)",
              margin: "12px 0",
            }}
          />
        </Grid>

        {/* Platforms - Autocomplete with Chips */}
        <Grid item xs={12} sm={6} style={{ paddingRight: "8px" }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Platforms
          </Typography>
          <Autocomplete
            multiple
            id="platform-selector"
            options={platformOptions}
            className={classes.autocomplete}
            value={selectedPlatforms}
            getOptionLabel={(option: PlatformOption) => option.label}
            onChange={handlePlatformChange}
            renderTags={(value: PlatformOption[], getTagProps) =>
              value.map((option: PlatformOption, index: number) => (
                <Chip
                  key={option.id}
                  variant="outlined"
                  label={option.label}
                  style={{
                    backgroundColor:
                      theme.palette.background.entity_highlight_background,
                    border: `1px solid ${theme.palette.background.entity_border}`,
                    margin: 2,
                  }}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
          />
        </Grid>

        {/* Order Channels - Autocomplete with Chips */}
        <Grid
          item
          xs={12}
          sm={6}
          style={{ paddingLeft: "8px", paddingRight: "20px" }}
        >
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Order Channels
          </Typography>
          <Autocomplete
            multiple
            id="channel-selector"
            options={channelOptions}
            className={classes.autocomplete}
            value={selectedChannels}
            getOptionLabel={(option: ChannelOption) => option.label}
            onChange={handleChannelChange}
            renderTags={(value: ChannelOption[], getTagProps) =>
              value.map((option: ChannelOption, index: number) => (
                <Chip
                  key={option.id}
                  variant="outlined"
                  label={option.label}
                  style={{
                    backgroundColor:
                      theme.palette.background.entity_highlight_background,
                    border: `1px solid ${theme.palette.background.entity_border}`,
                    margin: 2,
                  }}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider
            style={{
              backgroundColor: "rgba(255,255,255,0.08)",
              margin: "12px 0",
            }}
          />
        </Grid>

        {/* Availability selection modal */}
        <Grid item xs={12} style={{ marginRight: "32px", marginTop: "12px" }}>
          <AvailabilitySelection
            nodeData={availabilityList}
            handleSubmit={handleSubmitAvailability}
            setAvailabilityErrorMessage={setAvailabilityErrorMessage}
          />
        </Grid>

        {/* Availability submit button */}
        <Grid
          item
          xs={12}
          style={{
            marginBottom: "16px",
            display: "flex",
            justifyContent: "end",
            marginRight: "28px",
            marginTop: "12px",
          }}
        >
          <div style={{ width: "160px" }}>
            <ButtonCommon
              style={{
                flex: 1,
                fontSize: 11,
                width: "160px",
              }}
              disabled={handleDisable()}
              variant="contained"
              color={"green"}
              onClick={handleSubmitData}
            >
              {submitButtonName.UPDATE_SUBMIT_BUTTON}
            </ButtonCommon>
          </div>
        </Grid>
      </Grid>
    </CardCommon>
  );
};

export default withAuthority(AvailabilityModal, Authorities.MENU_READ);
